import Button from '@betterfunnel/ui-core/Button';
import TextField from '@betterfunnel/ui-core/TextField';
import { useDebounce } from '@betterfunnel/react-hooks/useDebounce';
import { SSOProvider } from '@betterfunnel/types';
import { EnvelopeIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
} from 'firebase/auth';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { auth } from '../Firebase';
import loginBanner from '../assets/images/loginBanner.avif';
import logo from '../assets/images/logo.png';
import { LAST_USED_ACCOUNT_STORAGE_KEY } from '../constants/storage';
import { AuthContext } from '../contexts/AuthContext';
import { track } from '../utils/analytics';
import { emailIsValid } from '../utils/validator';

const Login = () => {
  const {
    isAuthenticated,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithEmailLink,
    sendLoginLinkToEmail,
    loginWithSAML,
    getSSOAuthProvider,
  } = useContext(AuthContext);

  const location = useLocation();
  const params = useParams();

  const [, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [showSendLoginLinkToEmail, setShowSendLoginLinkToEmail] =
    useState<boolean>(false);
  const [showSSO, setShowSSO] = useState<{
    validEmail: boolean;
    authProvider?: SSOProvider;
  }>();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | undefined>();

  const debouncedEmail = useDebounce(email, 500);

  const navigate = useNavigate();

  const postLogin = useCallback(async () => {
    const lastUsedWorkspace = window.localStorage.getItem(
      LAST_USED_ACCOUNT_STORAGE_KEY,
    );
    if (lastUsedWorkspace) {
      navigate(`/workspaces/${lastUsedWorkspace}`);
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    // Confirm the link is a sign-in with email link.
    if (isAuthenticated) {
      postLogin();
    } else {
      const auth = getAuth();
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        } else {
          loginWithEmailLink(auth, email, window.location.href);
        }
      }
    }
  }, [
    location.pathname,
    params,
    isAuthenticated,
    postLogin,
    loginWithEmailLink,
  ]);

  useEffect(() => {
    if (!debouncedEmail) {
      return;
    }
    const isValid = emailIsValid(debouncedEmail);
    setShowSSO({ validEmail: isValid });

    if (isValid) {
      let relevant = true;
      getSSOAuthProvider(debouncedEmail).then((authProvider) => {
        if (!relevant) return;

        setShowSSO({ validEmail: isValid, authProvider });
      });

      return () => {
        relevant = false;
      };
    }
  }, [debouncedEmail, getSSOAuthProvider]);

  const onLogin = async (
    provider: 'google' | 'microsoft' | 'email' | SSOProvider,
  ) => {
    track('login_button_click', { provider });

    setError(undefined);
    setLoading(true);
    if (provider === 'google') {
      loginWithGoogle()
        .then(() => postLogin())
        .finally(() => setLoading(false));
    } else if (provider === 'microsoft') {
      loginWithMicrosoft()
        .then(() => postLogin())
        .finally(() => setLoading(false));
    } else if (provider === 'email') {
      if (!emailIsValid(email)) {
        return;
      }
      sendLoginLinkToEmail(email)
        .then(() => {
          window.localStorage.setItem('emailForSignIn', email);
          setMessage('Login link sent to your email');
        })
        .finally(() => setLoading(false));
    } else if ('id' in provider) {
      loginWithSAML(provider.id)
        .then(() => postLogin())
        .finally(() => setLoading(false));
    } else {
      console.error('Unknown provider', provider);
    }
  };

  useEffect(() => {
    getRedirectResult(auth)
      .catch((e) => {
        setError(e.message);
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    track('login_screen_view');
  }, []);

  return (
    <div className="flex min-h-full flex-1 bg-white">
      <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <img className="h-10 w-auto" src={logo} alt="BetterFunnel" />
            <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
              Sign in to your account
            </h2>
            {/* <p className="mt-2 text-sm leading-6 text-gray-500">
              Not a member?{' '}
              <a
                href="#"
                className="font-semibold text-indigo-600 hover:text-indigo-500"
              >
                Start a 14 day free trial
              </a>
            </p> */}
          </div>

          <div className="mt-5">
            {/* <div>
              <form action="#" method="POST" className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label
                      htmlFor="remember-me"
                      className="ml-3 block text-sm leading-6 text-gray-700"
                    >
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm leading-6">
                    <a
                      href="#"
                      className="font-semibold text-indigo-600 hover:text-indigo-500"
                    >
                      Forgot password?
                    </a>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div> */}

            <div className="mt-5">
              {/* <div className="relative">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-white px-6 text-gray-900">
                    Or continue with
                  </span>
                </div>
              </div> */}

              <div className="mt-6 grid gap-4">
                <button
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-400 px-3 py-2 text-black hover:bg-gray-50 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0]"
                  onClick={() => onLogin('google')}
                >
                  <svg
                    className="h-5 w-5"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    ></path>
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    ></path>
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    ></path>
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    ></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                  <span className="text-sm font-semibold leading-6">
                    Sign in with Google
                  </span>
                </button>
                <button
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-400 px-3 py-2 text-black hover:bg-gray-50 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0]"
                  onClick={() => onLogin('microsoft')}
                >
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    width="48px"
                    height="48px"
                  >
                    <path
                      fill="#ff5722"
                      d="M6 6H22V22H6z"
                      transform="rotate(-180 14 14)"
                    />
                    <path
                      fill="#4caf50"
                      d="M26 6H42V22H26z"
                      transform="rotate(-180 34 14)"
                    />
                    <path
                      fill="#ffc107"
                      d="M26 26H42V42H26z"
                      transform="rotate(-180 34 34)"
                    />
                    <path
                      fill="#03a9f4"
                      d="M6 26H22V42H6z"
                      transform="rotate(-180 14 34)"
                    />
                  </svg>
                  <span className="text-sm font-semibold leading-6">
                    Sign in with Microsoft
                  </span>
                </button>
                <button
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-400 px-3 py-2 text-black hover:bg-gray-50 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0]"
                  onClick={() => setShowSendLoginLinkToEmail(true)}
                >
                  <EnvelopeIcon className="h-5 w-5" />
                  <span className="text-sm font-semibold leading-6">
                    Sign in with email
                  </span>
                </button>
                <button
                  className="flex w-full items-center justify-center gap-3 rounded-md px-3 py-2 text-black hover:bg-gray-50 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0]"
                  onClick={() => {
                    setShowSSO({ validEmail: false });
                  }}
                >
                  <span className="text-sm font-semibold leading-6">
                    Sign in With SSO
                  </span>
                </button>

                {showSendLoginLinkToEmail && (
                  <div className="flex w-full flex-col gap-4">
                    <TextField
                      key={'emailinkfield'}
                      title="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      validation={
                        email === ''
                          ? {}
                          : {
                              status: !emailIsValid(email)
                                ? 'error'
                                : 'success',
                              text: !emailIsValid(email)
                                ? 'Invalid email format'
                                : '',
                            }
                      }
                    />
                    <Button
                      disabled={!emailIsValid(email)}
                      onClick={() => {
                        track('login_button_click', { provider: 'email' });
                        onLogin('email');
                      }}
                      loading={loading}
                    >
                      Send login link to my email
                    </Button>
                    <div>
                      {message && (
                        <p className="text-sm text-green-600">{message}</p>
                      )}
                    </div>
                  </div>
                )}

                {!!showSSO && (
                  <div className="flex w-full flex-col gap-4">
                    <TextField
                      key={'ssofield'}
                      title="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      validation={
                        email === ''
                          ? {}
                          : {
                              status: !emailIsValid(email)
                                ? 'error'
                                : 'success',
                              text: !emailIsValid(email)
                                ? 'Invalid email format'
                                : '',
                            }
                      }
                    />
                    <Button
                      disabled={!emailIsValid(email) || !showSSO.authProvider}
                      onClick={() => {
                        if (!showSSO.authProvider) {
                          return;
                        }
                        track('login_with_sso_provider_button_click', {
                          provider: 'email',
                        });
                        onLogin(showSSO.authProvider);
                      }}
                      loading={loading}
                    >
                      Sign In
                    </Button>
                    <div>
                      {message && (
                        <p className="text-sm text-green-600">{message}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <p className="mt-6 text-xs text-gray-500">
                By logging in or signing up, you agree to abide by our policies,
                including our{' '}
                <a
                  href="https://betterfunnel.ai/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://betterfunnel.ai/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="relative hidden w-0 flex-1 lg:block">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src={loginBanner}
          alt=""
        />
      </div>
    </div>
  );
};

export default Login;
